import React, { useEffect, useMemo, useState, useRef, useCallback, useContext } from 'react';
import watermark from '../../assets/images&icons/watermark.png';
import { Link, useNavigate } from 'react-router-dom';
import { IContent } from '../../interfaces';
import { format } from 'date-fns';
import { axios } from '../..';
import { useDispatch } from 'react-redux';
import microphoneIcon from '../../assets/images&icons/microphoneIcon.png';
import LightLogo from '../Layout/Logo/LightLogo';
import ChevronDown from '../Atomos/Icons/ChevronDown';
import ChevronRight from '../Atomos/Icons/ChevronRight';
import ChevronLeft from '../Atomos/Icons/ChevronLeft';
import heroVideo from '../../assets/video/hero.mp4';
import asSeenIcon from '../../assets/images&icons/asseen-home.png';
import collabIcon from '../../assets/images&icons/collabIcon.png';
import insightsIcon from '../../assets/images&icons/insightsIcon.png';
import webinarsImg from '../../assets/images&icons/webinars.png';
import cardBg1 from '../../assets/images&icons/bg-landing-card-1.png';
import cardBg2 from '../../assets/images&icons/bg-landing-card-2.png';
import cardBg3 from '../../assets/images&icons/bg-landing-card-3.png';
import dotPattern from '../../assets/images&icons/dot-pattern.png';
import styles from './LandingPage.module.css';
import LandingHeader from '../Layout/Header/LandingHeader';
import LandingMenu from '../Layout/Header/LandingMenu';
import { setScrollPosition } from '../../redux';
import Footer from '../Layout/Footer/Footer';
import Carousel from 'react-multi-carousel';
import { formatEventDate as formatLandingEventDate } from '../../utils/eventDateFormatter';

const removeLeadingP = (value: string | number | undefined): string => {
  if (value === null || value === undefined) {
    return "";
  }
  const str = String(value);
  return str.startsWith("P") ? str.substring(1) : str;
};

const truncateByWords = (value: string | undefined, maxChars: number = 70): string => {
  const text = (value || '').trim().replace(/\s+/g, ' ');
  if (text.length <= maxChars) {
    return text;
  }

  const ellipsis = '...';
  const limit = maxChars - ellipsis.length;
  const words = text.split(' ');
  let result = '';

  for (const word of words) {
    const next = result ? `${result} ${word}` : word;
    if (next.length > limit) {
      break;
    }
    result = next;
  }

  if (!result) {
    return `${text.slice(0, limit).trimEnd()}${ellipsis}`;
  }

  return `${result}${ellipsis}`;
};

const getLegacyId = (content: any): string => {
  if (content.legacyId) {
    return content.legacyId;
  }
  if (content.shortId) {
    return removeLeadingP(content.shortId);
  }
  if (content.id) {
    return content.id;
  }
  return "";
};

const HARDCODED_INSIGHTS: Partial<IContent>[] = [
  { title: 'GC: In-House Technology - Europe Edition', link: '/gcinsights/in-house-legal-tech-trends-2019' },
  { title: 'GC: In-House Technology - Asia Pacific Edition', link: '/gcinsights/in-house-legal-tech-trends-asiapacific-2020' },
  { title: 'GC: In-House Technology – Latin America Edition', link: '/gcinsights/in-house-legal-tech-trends-latinamerica-2020' },
  { title: 'GC: In-House Technology – North America Edition', link: '/gcinsights/in-house-legal-tech-trends-northamerica-2021' },
  { title: 'GC: In-House Technology – Global Edition', link: '/gcinsights/in-house-legal-tech-trends-global-2022' },
];

const HARDCODED_SUCCESS_STORIES: Partial<IContent>[] = [
  { title: 'WSG Members Heuking Kühn Lüer Wojtek and Shoosmiths Collaborate on DMB’s Acquisition of Raytheon Anschütz GmbH', link: '/success_stories.asp' },
  { title: 'WSG Members Lavery Lawyers and Waller Collaborate on Premier Parks LLC Purchase of Calypso-Valcartier Group’s Shares', link: '/success_stories.asp' },
  { title: 'Eight WSG Member Firms Collaborate to Advise EasyPark on Global Acquisition', link: '/success_stories.asp' },
  { title: 'WSG Members Heuking Kühn Lüer Wojtek and Garrigues Advise SALTO Systems on Acquisition of GANTNER Electronic Group', link: '/success_stories.asp' },
  { title: 'WSG Latin America Member Firms Represent Transtelco in Multijurisdictional Acquisition of Neutrona Networks', link: '/success_stories.asp' },
  { title: 'Asters Collaborates with Deacons and Wins Stockholm Arbitration for ASK Technology Limited', link: '/success_stories.asp' },
];

const BASE_WIDTH = 1440;
const MAX_SCALE = 1920 / BASE_WIDTH; 
const MIN_SCALE = 0.6; 

function useInView(threshold: number = 0.3): [React.RefObject<HTMLElement>, boolean] {
  const ref = useRef<HTMLElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect(); 
        }
      },
      { threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
}

function useCountUp(end: number, duration: number = 3000, shouldStart: boolean = true): number {
  const [count, setCount] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const hasStartedRef = useRef(false);

  const animate = useCallback((timestamp: number) => {
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
    const easeOut = 1 - Math.pow(1 - progress, 3);
    setCount(Math.floor(easeOut * end));

    if (progress < 1) {
      rafRef.current = requestAnimationFrame(animate);
    } else {
      setCount(end);
    }
  }, [end, duration]);

  useEffect(() => {
    if (end > 0 && shouldStart && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startTimeRef.current = null;
      rafRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [end, animate, shouldStart]);

  return count;
}

interface AnimatedStatItemProps {
  label: string;
  value: number;
  format: (v: number) => string;
  isInView: boolean;
}

const AnimatedStatItem: React.FC<AnimatedStatItemProps> = ({ label, value, format, isInView }) => {
  const animatedValue = useCountUp(value, 3000, isInView);
  
  return (
    <div className={styles.statItem}>
      <div className={styles.statValue}>{format(animatedValue)}</div>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statLine} />
    </div>
  );
};

export interface LandingPageStats {
  jurisdictions: number;
  professionals: number;
  memberFirms: number;
  serviceAreas: number;
}

export interface LandingPageInitialData {
  pressReleases: IContent[];
  articles: IContent[];
  insights: IContent[];
  collaborations: IContent[];
  events: any[];
  stats: LandingPageStats;
}

export interface LandingPageProps {
  initialData?: LandingPageInitialData;
}

const LandingPage: React.FC<LandingPageProps> = ({ initialData }) => {
    const [landingData, setLandingData] = useState<LandingPageInitialData | null>(initialData || null);
    const [currentInsightIndex, setCurrentInsightIndex] = useState(0);
    const currentInsightIndexRef = useRef(0);
    const [isInsightTransitioning, setIsInsightTransitioning] = useState(false);
    const [insightDirection, setInsightDirection] = useState<'left' | 'right'>('right');
    const [pendingInsightIndex, setPendingInsightIndex] = useState<number | null>(null);
    const [previousInsightIndex, setPreviousInsightIndex] = useState<number | null>(null);
    const insightCardRef = React.useRef<HTMLDivElement>(null);
    const [insightCardWidth, setInsightCardWidth] = React.useState(0);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : BASE_WIDTH);
    useEffect(() => {
      function handleResize() {
        setWindowWidth(window.innerWidth);
      }
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [currentCollabIndex, setCurrentCollabIndex] = useState(0);
    const currentCollabIndexRef = useRef(0);
    const [isCollabTransitioning, setIsCollabTransitioning] = useState(false);
    const [collabDirection, setCollabDirection] = useState<'left' | 'right'>('right');
    const [pendingCollabIndex, setPendingCollabIndex] = useState<number | null>(null);
    const [previousCollabIndex, setPreviousCollabIndex] = useState<number | null>(null);
    const collabCardRef = React.useRef<HTMLDivElement>(null);
    const [collabCardWidth, setCollabCardWidth] = useState(0);

    useEffect(() => {
      function handleResize() {
        setWindowWidth(window.innerWidth);
      }
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
      setCurrentEventIndex(0);
    }, [landingData && landingData.events, windowWidth]);
    useEffect(() => {
      if (!insightCardRef.current) {
        return;
      }
      const handleResize = () => {
        if (insightCardRef.current) {
          setInsightCardWidth(insightCardRef.current.offsetWidth);
        }
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [windowWidth]);

    useEffect(() => {
      if (!articleCardRef.current) {
        return;
      }
      const handleResize = () => {
        if (articleCardRef.current) {
          setArticleCardWidth(articleCardRef.current.offsetWidth);
        }
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [windowWidth]);

    useEffect(() => {
      if (!collabCardRef.current) {
        return;
      }
      const handleResize = () => {
        if (collabCardRef.current) {
          setCollabCardWidth(collabCardRef.current.offsetWidth);
        }
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [windowWidth]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  const [previousHeroSlide, setPreviousHeroSlide] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [previousNewsIndex, setPreviousNewsIndex] = useState<number | null>(null);
  const [isNewsTransitioning, setIsNewsTransitioning] = useState(false);
  const [newsDirection, setNewsDirection] = useState<'left' | 'right'>('right');
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);
  const currentArticleIndexRef = useRef(0);
  const [isArticleTransitioning, setIsArticleTransitioning] = useState(false);
  const [articleDirection, setArticleDirection] = useState<'left' | 'right'>('right');
  const [pendingArticleIndex, setPendingArticleIndex] = useState<number | null>(null);
  const [previousArticleIndex, setPreviousArticleIndex] = useState<number | null>(null);
  const articleCardRef = React.useRef<HTMLDivElement>(null);
  const [articleCardWidth, setArticleCardWidth] = useState(0);
  const featuresRowRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollTopBottom, setScrollTopBottom] = useState(24);
  const footerRef = useRef<HTMLElement>(null);

  const insightItems = useMemo(() => HARDCODED_INSIGHTS, []);
  const successStoryItems = useMemo(() => HARDCODED_SUCCESS_STORIES, []);
    useEffect(() => {
      if (
        insightItems.length <= 1 ||
        !landingData || !landingData.articles || landingData.articles.length <= 1 ||
        successStoryItems.length <= 1
      ) {
        return;
      }
      const interval = setInterval(() => {
        autoAdvanceAllCards();
      }, 5000);
      return () => clearInterval(interval);
    }, [insightItems.length, landingData?.articles?.length, successStoryItems.length]);

    const autoAdvanceAllCards = useCallback(() => {
      const curInsight = currentInsightIndexRef.current;
      const curArticle = currentArticleIndexRef.current;
      const curCollab = currentCollabIndexRef.current;

      setInsightDirection('right');
      setPreviousInsightIndex(curInsight);
      const nextInsightIndex = (curInsight + 1) % Math.max(insightItems.length, 1);
      setPendingInsightIndex(nextInsightIndex);
      setIsInsightTransitioning(true);

      setArticleDirection('right');
      setPreviousArticleIndex(curArticle);
      const nextArticleIndex = (curArticle + 1) % Math.max(landingData?.articles?.length ?? 1, 1);
      setPendingArticleIndex(nextArticleIndex);
      setIsArticleTransitioning(true);

      setCollabDirection('right');
      setPreviousCollabIndex(curCollab);
      const nextCollabIndex = (curCollab + 1) % Math.max(successStoryItems.length, 1);
      setPendingCollabIndex(nextCollabIndex);
      setIsCollabTransitioning(true);

      setTimeout(() => {
        currentInsightIndexRef.current = nextInsightIndex;
        setCurrentInsightIndex(nextInsightIndex);
        setIsInsightTransitioning(false);
        setTimeout(() => {
          setPreviousInsightIndex(null);
          setPendingInsightIndex(null);
        }, 10);

        currentArticleIndexRef.current = nextArticleIndex;
        setCurrentArticleIndex(nextArticleIndex);
        setIsArticleTransitioning(false);
        setTimeout(() => {
          setPreviousArticleIndex(null);
          setPendingArticleIndex(null);
        }, 10);

        currentCollabIndexRef.current = nextCollabIndex;
        setCurrentCollabIndex(nextCollabIndex);
        setIsCollabTransitioning(false);
        setTimeout(() => {
          setPreviousCollabIndex(null);
          setPendingCollabIndex(null);
        }, 10);
      }, 500);
    }, [insightItems.length, landingData?.articles?.length, successStoryItems.length]);
  const featureItems = useMemo(
    () => [
      { title: 'Seamless Collaboration', pos: 'center' },
      { title: 'Robust Access to Information', pos: 'center' },
      { title: 'Collaborations Across Industries', pos: 'center' },
      { title: 'Distinguished Select Membership', pos: 'center' },
      { title: 'Proprietary Solutions Platform', pos: 'center' },
    ],
    []
  );
  const loopedFeatures = useMemo(() => [...featureItems, ...featureItems, ...featureItems], [featureItems]);

  const cardBgs = [cardBg1, cardBg2, cardBg3];

  const currentFeatureIndexRef = useRef(featureItems.length);

  const [statsRef, statsInView] = useInView(0.3);
  
  const hasInitialData = !!initialData;

  const heroSlides = [
    'Authentic Association & Seamless Collaboration',
    'Uniting Professionals, Connecting the World',
    'Global Expertise Powered by Digital Innovation',
  ];

  const handleInsightNext = useCallback(() => {
    setInsightDirection('right');
    setPreviousInsightIndex(currentInsightIndex);
    const nextIndex = (currentInsightIndex + 1) % Math.max(insightItems.length, 1);
    setPendingInsightIndex(nextIndex);
    setIsInsightTransitioning(true);
    setTimeout(() => {
      currentInsightIndexRef.current = nextIndex;
      setCurrentInsightIndex(nextIndex);
      setIsInsightTransitioning(false);
      setTimeout(() => {
        setPreviousInsightIndex(null);
        setPendingInsightIndex(null);
      }, 10);
    }, 500);
  }, [currentInsightIndex, insightItems.length]);
  const handleInsightPrev = useCallback(() => {
    setInsightDirection('left');
    setPreviousInsightIndex(currentInsightIndex);
    const prevIndex = (currentInsightIndex - 1 + Math.max(insightItems.length, 1)) % Math.max(insightItems.length, 1);
    setPendingInsightIndex(prevIndex);
    setIsInsightTransitioning(true);
    setTimeout(() => {
      currentInsightIndexRef.current = prevIndex;
      setCurrentInsightIndex(prevIndex);
      setIsInsightTransitioning(false);
      setTimeout(() => {
        setPreviousInsightIndex(null);
        setPendingInsightIndex(null);
      }, 10);
    }, 500);
  }, [currentInsightIndex, insightItems.length]);

  const handleArticleNext = useCallback(() => {
    setArticleDirection('right');
    setPreviousArticleIndex(currentArticleIndex);
    const nextIndex = (currentArticleIndex + 1) % Math.max(landingData?.articles?.length ?? 1, 1);
    setPendingArticleIndex(nextIndex);
    setIsArticleTransitioning(true);
    setTimeout(() => {
      currentArticleIndexRef.current = nextIndex;
      setCurrentArticleIndex(nextIndex);
      setIsArticleTransitioning(false);
      setTimeout(() => {
        setPreviousArticleIndex(null);
        setPendingArticleIndex(null);
      }, 10);
    }, 500);
  }, [currentArticleIndex, landingData?.articles?.length]);
  const handleArticlePrev = useCallback(() => {
    setArticleDirection('left');
    setPreviousArticleIndex(currentArticleIndex);
    const prevIndex = (currentArticleIndex - 1 + Math.max(landingData?.articles?.length ?? 1, 1)) % Math.max(landingData?.articles?.length ?? 1, 1);
    setPendingArticleIndex(prevIndex);
    setIsArticleTransitioning(true);
    setTimeout(() => {
      currentArticleIndexRef.current = prevIndex;
      setCurrentArticleIndex(prevIndex);
      setIsArticleTransitioning(false);
      setTimeout(() => {
        setPreviousArticleIndex(null);
        setPendingArticleIndex(null);
      }, 10);
    }, 500);
  }, [currentArticleIndex, landingData?.articles?.length]);

  const handleCollabNext = useCallback(() => {
    setCollabDirection('right');
    setPreviousCollabIndex(currentCollabIndex);
    const nextIndex = (currentCollabIndex + 1) % Math.max(successStoryItems.length, 1);
    setPendingCollabIndex(nextIndex);
    setIsCollabTransitioning(true);
    setTimeout(() => {
      currentCollabIndexRef.current = nextIndex;
      setCurrentCollabIndex(nextIndex);
      setIsCollabTransitioning(false);
      setTimeout(() => {
        setPreviousCollabIndex(null);
        setPendingCollabIndex(null);
      }, 10);
    }, 500);
  }, [currentCollabIndex, successStoryItems.length]);
  const handleCollabPrev = useCallback(() => {
    setCollabDirection('left');
    setPreviousCollabIndex(currentCollabIndex);
    const prevIndex = (currentCollabIndex - 1 + Math.max(successStoryItems.length, 1)) % Math.max(successStoryItems.length, 1);
    setPendingCollabIndex(prevIndex);
    setIsCollabTransitioning(true);
    setTimeout(() => {
      currentCollabIndexRef.current = prevIndex;
      setCurrentCollabIndex(prevIndex);
      setIsCollabTransitioning(false);
      setTimeout(() => {
        setPreviousCollabIndex(null);
        setPendingCollabIndex(null);
      }, 10);
    }, 500);
  }, [currentCollabIndex, successStoryItems.length]);

  const goToInsightPage = useCallback((link?: string) => {
    if (!link) {
      return;
    }
    navigate(link);
  }, [navigate]);

  const scrollFeatures = useCallback(
    (direction: 'left' | 'right') => {
      const el = featuresRowRef.current;
      if (!el) {
        return;
      }
      const item = el.querySelector('[data-feature-item="true"]') as HTMLElement | null;
      if (!item) {
        return;
      }
      const itemWidth = item.clientWidth;
      const baseOffset = item.offsetLeft;
      const setLength = featureItems.length;
      const firstSetStart = setLength;
      const secondSetStart = setLength * 2;
      let currentIndex = currentFeatureIndexRef.current;

      if (direction === 'right' && currentIndex >= secondSetStart - 1) {
        currentIndex -= setLength;
        currentFeatureIndexRef.current = currentIndex;
        el.scrollLeft = baseOffset + currentIndex * itemWidth;
      } else if (direction === 'left' && currentIndex <= firstSetStart) {
        currentIndex += setLength;
        currentFeatureIndexRef.current = currentIndex;
        el.scrollLeft = baseOffset + currentIndex * itemWidth;
      }

      const nextIndex = direction === 'right' ? currentIndex + 1 : currentIndex - 1;
      currentFeatureIndexRef.current = nextIndex;
      window.requestAnimationFrame(() => {
        el.scrollTo({ left: baseOffset + nextIndex * itemWidth, behavior: 'smooth' });
      });
    },
    [featureItems.length]
  );

  useEffect(() => {
    const el = featuresRowRef.current;
    if (!el) {
      return;
    }
    const item = el.querySelector('[data-feature-item="true"]') as HTMLElement | null;
    if (!item) {
      return;
    }

    const itemWidth = item.clientWidth;
    const baseOffset = item.offsetLeft;
    el.scrollLeft = baseOffset + currentFeatureIndexRef.current * itemWidth;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scale = useMemo(() => {
    const raw = windowWidth / BASE_WIDTH;
    return Math.min(Math.max(raw, MIN_SCALE), MAX_SCALE);
  }, [windowWidth]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (landingData) {
      return;
    }
    axios.Get('/landing-data')
      .then((response: any) => {
        if (response.data && response.data.stats) {
          setLandingData(response.data as LandingPageInitialData);
        }
      })
      .catch(() => {});
  }, [landingData]);

  const changeSlide = useCallback((newSlide: number) => {
    setPreviousHeroSlide(currentHeroSlide);
    setIsTransitioning(true);
    setCurrentHeroSlide(newSlide);
    setTimeout(() => {
      setIsTransitioning(false);
      setPreviousHeroSlide(null);
    }, 600);
  }, [currentHeroSlide]);

  const changeNewsIndex = useCallback((newIndex: number, direction?: 'left' | 'right') => {
    const calculatedDirection = direction || (newIndex > currentNewsIndex ? 'right' : 'left');
    setPreviousNewsIndex(currentNewsIndex);
    setNewsDirection(calculatedDirection);
    setIsNewsTransitioning(true);
    setCurrentNewsIndex(newIndex);
    setTimeout(() => {
      setIsNewsTransitioning(false);
      setPreviousNewsIndex(null);
    }, 500);
  }, [currentNewsIndex]);

  useEffect(() => {
    const heroInterval = setInterval(() => {
      changeSlide((currentHeroSlide + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(heroInterval);
  }, [currentHeroSlide, changeSlide]);

  useEffect(() => {
    if (!(landingData?.pressReleases?.length)) {
      return;
    }
    const newsInterval = setInterval(() => {
      const nextIndex = (currentNewsIndex + 1) % landingData.pressReleases.length;
      changeNewsIndex(nextIndex, 'right');
    }, 5000);
    return () => clearInterval(newsInterval);
  }, [currentNewsIndex, landingData?.pressReleases?.length, changeNewsIndex]);

  useEffect(() => {
    const findScrollContainer = (): HTMLElement | Window => {
      const contentContainer = document.getElementById('content-container');
      if (contentContainer) {
        return contentContainer;
      }
      
      const landingRoot = document.querySelector('[class*="LandingPage_root"]');
      if (landingRoot) {
        let parent = landingRoot.parentElement;
        while (parent) {
          const style = window.getComputedStyle(parent);
          if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            return parent;
          }
          parent = parent.parentElement;
        }
      }
      
      return window;
    };

    const scrollEl = findScrollContainer();
    const buttonHeight = 56 * scale;
    const defaultBottom = 24 * scale;
    
    const calculateButtonPosition = () => {
      if (footerRef.current) {
        const footerRect = footerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        if (footerRect.top < viewportHeight) {
          const footerVisibleHeight = viewportHeight - footerRect.top;
          const newBottom = footerVisibleHeight - (buttonHeight / 2);
          return Math.max(newBottom, defaultBottom);
        }
      }
      return defaultBottom;
    };
    
    let ticking = false;
    let lastScrollY = 0;
    
    const onScroll = () => {
      const y =
        scrollEl === window
          ? window.scrollY
          : (scrollEl as HTMLElement).scrollTop;
      
      lastScrollY = y;
      
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const newPosition = calculateButtonPosition();
          const shouldShow = lastScrollY > 900 * scale;
          
          setScrollTopBottom(prev => prev !== newPosition ? newPosition : prev);
          setShowScrollTop(prev => prev !== shouldShow ? shouldShow : prev);
          
          dispatch(setScrollPosition({ pathname: '/', scrollPosition: lastScrollY }));
          
          ticking = false;
        });
        ticking = true;
      }
    };
    
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
    };
  }, [scale, dispatch]);

  const formatPressDate = (date: Date | string | undefined): string => {
    if (!date) {
      return '';
    }
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return format(dateObj, 'EEEE, MMMM d, yyyy');
    } catch {
      return '';
    }
  };

  const goToTop = () => {
    const contentContainer = document.getElementById('content-container');
    if (contentContainer) {
      contentContainer.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    const landingRoot = document.querySelector('[class*="LandingPage_root"]');
    if (landingRoot) {
      let parent = landingRoot.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          parent.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        parent = parent.parentElement;
      }
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = featuresRowRef.current;
    if (!el) {
      return;
    }
    const item = el.querySelector('[data-feature-item="true"]') as HTMLElement | null;
    if (!item) {
      return;
    }

    const itemWidth = item.clientWidth;
    const baseOffset = item.offsetLeft;
    el.scrollLeft = baseOffset + currentFeatureIndexRef.current * itemWidth;
  }, [windowWidth]);

  useEffect(() => {
    if (featureItems.length <= 1) {
      return;
    }
    const autoplayInterval = window.setInterval(() => {
      scrollFeatures('right');
    }, 5000);

    return () => {
      window.clearInterval(autoplayInterval);
    };
  }, [featureItems.length, scrollFeatures]);

  const eventsPerView = useMemo(() => {
    if (windowWidth >= 1200) {
      return 3;
    } 
    if (windowWidth >= 768) {
      return 2;
    }
    return 1;
  }, [windowWidth]);

  const maxEventIndex = useMemo(() => {
    const totalEvents = landingData?.events?.length && landingData.events.length > 0 ? Math.min(landingData.events.length, 3) : 3;
    return Math.max(0, totalEvents - eventsPerView);
  }, [eventsPerView, landingData?.events?.length]);

  useEffect(() => {
    if (currentEventIndex > maxEventIndex) {
      setCurrentEventIndex(maxEventIndex);
    }
  }, [currentEventIndex, maxEventIndex]);

  return (
    <div className={styles.root} style={{ ['--s' as any]: scale }}>
      <LandingHeader />
      <LandingMenu />
      <section className={styles.hero}>
        <video
          autoPlay
          loop
          muted
          playsInline
          className={styles.heroVideo}
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        <div className={styles.heroOverlay} />

        <div className={`${styles.content} ${styles.heroContent}`}>
          <div className={styles.heroInner}>
            <Link to="/" className={styles.heroBannerLogo} aria-label="Home">
              <div className={`${styles.heroLogo} hero-logo`}>
                <LightLogo />
              </div>
            </Link>
            <div className={styles.heroTitleWrapper}>
              {previousHeroSlide !== null && isTransitioning && (
                <h1 className={`${styles.heroTitle} ${styles.heroTitleOut}`}>
                  {heroSlides[previousHeroSlide]}
                </h1>
              )}
              <h1 className={`${styles.heroTitle} ${isTransitioning ? styles.heroTitleIn : ''}`}>
                {heroSlides[currentHeroSlide]}
              </h1>
            </div>

            <div className={styles.heroControls}>
              <div className={styles.dots}>
                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Slide ${i + 1}`}
                    onClick={() => changeSlide(i)}
                    className={`${styles.dot} ${i === currentHeroSlide ? styles.dotActive : ''}`}
                  />
                ))}
              </div>

              <button
                type="button"
                className={styles.exploreButton}
                onClick={() => document.getElementById('landing-about')?.scrollIntoView({ behavior: 'smooth' })}
              >
                EXPLORE
                <div style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff' }}>
                  <ChevronDown rotation="rotate(0deg)" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.newsBar} style={{ zIndex: 999}}>
        <div className={`${styles.content} ${styles.newsInner}`}>
          <div className={styles.newsLeft}>
            <div style={{ width: `calc(36px * var(--s))`, height: `calc(34.2px * var(--s))` }}>
              <img src={microphoneIcon} alt="Microphone" style={{ width: '90%', height: '90%', objectFit: 'contain' }} />
            </div>
            <span className={styles.newsLabel}>WSG MEMBER NEWS</span>
          </div>

          <div className={styles.newsDividerStrong} />

          <div 
            className={styles.newsMid}
            onClick={() => {
              if (landingData && (landingData.pressReleases?.length ?? 0) > 0) {
                const currentRelease = landingData.pressReleases[currentNewsIndex];
                const pressId = getLegacyId(currentRelease);
                if (pressId) {
                  navigate(`/releases.asp?action=press&pressid=${pressId}`);
                }
              }
            }}
            style={{ cursor: (landingData?.pressReleases?.length ?? 0) > 0 ? 'pointer' : 'default' }}
          >
            {previousNewsIndex !== null && isNewsTransitioning && (landingData?.pressReleases?.length ?? 0) > 0 && (
              <div className={`${styles.newsContent} ${newsDirection === 'right' ? styles.newsOutLeft : styles.newsOutRight}`}>
                <div className={styles.newsTextWrapper}>
                  <span className={styles.newsDate}>
                    {formatPressDate(landingData?.pressReleases?.[previousNewsIndex]?.publishedDate as Date)}
                  </span>
                  <span className={styles.newsPipe}>|</span>
                  <span className={styles.newsTitle}>
                    {landingData?.pressReleases?.[previousNewsIndex]?.title || ''}
                  </span>
                </div>
              </div>
            )}
            {(landingData?.pressReleases?.length ?? 0) > 0 ? (
              <div className={`${styles.newsContent} ${isNewsTransitioning ? (newsDirection === 'right' ? styles.newsInRight : styles.newsInLeft) : ''}`}>
                <div className={styles.newsTextWrapper}>
                  <span className={styles.newsDate}>
                    {formatPressDate(landingData?.pressReleases?.[currentNewsIndex]?.publishedDate as Date)}
                  </span>
                  <span className={styles.newsPipe}>|</span>
                  <span className={styles.newsTitle}>
                    {landingData?.pressReleases?.[currentNewsIndex]?.title || ''}
                  </span>
                </div>
              </div>
            ) : (
              <div className={styles.newsContent}>
                <div className={styles.newsTextWrapper}>
                  <span className={styles.newsDate}>Tuesday, January 12, 2025</span>
                  <span className={styles.newsPipe}>|</span>
                  <span className={styles.newsTitle}>
                    Han Kun Advises Zonsen PepLib Biotech Inc. on the Landmark Global Exclusive Licensing Collaboration with Novartis for a Radioligand Therapy Asset
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.newsDots}>
            {landingData?.pressReleases?.slice(0, 7).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`News item ${i + 1}`}
                onClick={() => changeNewsIndex(i)}
                className={`${styles.newsDot} ${i === currentNewsIndex ? styles.newsDotActive : ''}`}
              />
            ))}
          </div>

          <div className={styles.newsArrows}>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={() => changeNewsIndex(currentNewsIndex === 0 ? (landingData?.pressReleases?.length || 0) - 1 : currentNewsIndex - 1, 'left')}
              disabled={!(landingData?.pressReleases?.length)}
              aria-label="Previous news item"
            >
              <div style={{ width: '70%', height: '70%' }}>
                <ChevronLeft />
              </div>
            </button>
            <button
              type="button"
              className={styles.arrowBtn}
              onClick={() => changeNewsIndex(currentNewsIndex === (landingData?.pressReleases?.length || 0) - 1 ? 0 : currentNewsIndex + 1, 'right')}
              disabled={!(landingData?.pressReleases?.length)}
              aria-label="Next news item"
            >
              <div style={{ width: '70%', height: '70%' }}>
                <ChevronRight />
              </div>
            </button>
          </div>
        </div>
      </section>

      <section
        id="landing-about"
        style={{
          backgroundImage: `url(${watermark})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'left center',
          backgroundSize: 'auto',
          backgroundColor: '#F7F7F7',
          paddingTop: '-100px',
        }}
      >
        <div className={`${styles.about}`}>
          <h2 className={`${styles.eventsTitle} ${styles.textSecondary}`}>World Services Group</h2>
          <p className={styles.aboutText}>
            The most prominent network of elite independent legal firms with access to cross industry expertise that enables global solutions powered by seamless collaboration and innovation for valuable client needs.
          </p>
          <button type="button" onClick={() => navigate('/aboutus')} className={styles.linkButton}>
            LEARN MORE
            <span className={styles.linkDot} aria-hidden="true">
              <div style={{ width: '80%', height: '80%', color: '#ffffff' }}>
                <ChevronRight />
              </div>
            </span>
          </button>
        </div>
      </section>

      <section className={styles.sectionWhite}>
        <div className={`${styles.content} ${styles.cardsRow}`}>
          {[
            {
              title: 'As Seen In',
              icon: asSeenIcon,
              iconBg: '#BC3323',
              content: landingData?.articles?.[currentArticleIndex],
              link: '/publications.asp',
              buttonText: 'MORE ARTICLES',
              onPrev: handleArticlePrev,
              onNext: handleArticleNext,
              hasContent: (landingData?.articles?.length ?? 0) > 0,
              currentIndex: currentArticleIndex,
              totalCount: landingData?.articles?.length || 0
            },
            {
              title: 'Insights',
              icon: insightsIcon,
              iconBg: '#00549C',
              content: insightItems[currentInsightIndex % insightItems.length],
              link: '/gcinsights.asp',
              buttonText: 'MORE INSIGHTS',
              onPrev: handleInsightPrev,
              onNext: handleInsightNext,
              hasContent: insightItems.length > 0,
              currentIndex: currentInsightIndex,
              totalCount: insightItems.length
            },
            {
              title: 'WSG Collaborations',
              icon: collabIcon,
              iconBg: '#0070CD',
              content: successStoryItems[currentCollabIndex % successStoryItems.length],
              link: '/success_stories.asp',
              buttonText: 'MORE SUCCESS STORIES',
              onPrev: handleCollabPrev,
              onNext: handleCollabNext,
              hasContent: successStoryItems.length > 0,
              currentIndex: currentCollabIndex,
              totalCount: successStoryItems.length
            },
          ].map((card, index) => {
            let contentLink: string;
            if (index === 0) {
              const artLegacyId = card.content ? getLegacyId(card.content) : '';
              contentLink = artLegacyId
                ? `/publications.asp?action=article&artid=${artLegacyId}`
                : card.link;
            } else {
              contentLink = (card.content as any)?.link || card.link;
            }
            let isTransitioning = false;
            let direction: 'left' | 'right' = 'right';
            let dynamicStyle = {};
            if (index === 1) {
              if (isInsightTransitioning) {
                if (insightDirection === 'right') {
                  dynamicStyle = { transform: `translateX(-${insightCardWidth}px)`, transition: 'transform 0.5s' };
                } else {
                  dynamicStyle = { transform: `translateX(${insightCardWidth}px)`, transition: 'transform 0.5s' };
                }
              } else {
                dynamicStyle = { transform: 'translateX(0)', transition: 'transform 0.5s' };
              }
            }
            switch (index) {
              case 0:
                isTransitioning = isArticleTransitioning;
                direction = articleDirection;
                break;
              case 1:
                isTransitioning = isInsightTransitioning;
                direction = insightDirection;
                break;
              case 2:
                isTransitioning = isCollabTransitioning;
                direction = collabDirection;
                break;
              default:
                break;
            }
            let isCardTransitioning, cardDirection, cardRef, cardWidth, previousIndex, pendingIndex, itemsArr, cardKey, cardTitle;
            if (index === 0) {
              isCardTransitioning = isArticleTransitioning;
              cardDirection = articleDirection;
              cardRef = articleCardRef;
              cardWidth = articleCardWidth;
              previousIndex = previousArticleIndex;
              pendingIndex = pendingArticleIndex;
              itemsArr = landingData?.articles || [];
              cardKey = 'article';
              cardTitle = 'As Seen In';
            } else if (index === 1) {
              isCardTransitioning = isInsightTransitioning;
              cardDirection = insightDirection;
              cardRef = insightCardRef;
              cardWidth = insightCardWidth;
              previousIndex = previousInsightIndex;
              pendingIndex = pendingInsightIndex;
              itemsArr = insightItems;
              cardKey = 'insight';
              cardTitle = 'Insights';
            } else {
              isCardTransitioning = isCollabTransitioning;
              cardDirection = collabDirection;
              cardRef = collabCardRef;
              cardWidth = collabCardWidth;
              previousIndex = previousCollabIndex;
              pendingIndex = pendingCollabIndex;
              itemsArr = successStoryItems;
              cardKey = 'collab';
              cardTitle = 'WSG Collaborations';
            }
            let thisCardWidth = insightCardWidth;
            if (index === 0) {
              thisCardWidth = articleCardWidth;
            }
            if (index === 2) {
              thisCardWidth = collabCardWidth;
            }
            return (
              <div key={index} className={styles.card}>
                <button
                  type="button"
                  className={styles.cardSideArrow + ' ' + styles.cardSideArrowLeft}
                  onClick={card.onPrev}
                  disabled={!card.hasContent}
                  aria-label="Previous"
                  style={{ cursor: card.hasContent ? 'pointer' : 'default', opacity: card.hasContent ? 1 : 0.3 }}
                >
                  <div style={{ width: '70%', height: '70%' }}>
                    <ChevronLeft />
                  </div>
                </button>
                <div style={{position: 'relative', width: '100%', height: '100%'}}>
                  {isCardTransitioning && previousIndex !== null && (
                    <div
                      className={
                        styles.cardContentWrapper +
                        (cardDirection === 'right'
                          ? ' ' + styles.insightSlideOutLeft
                          : ' ' + styles.insightSlideOutRight)
                      }
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        zIndex: 1,
                        '--insight-card-width': `${insightCardWidth}px`
                      } as React.CSSProperties}
                      ref={null}
                    >
                      <div className={styles.cardIconCircle} style={{ background: card.iconBg }}>
                        <img className={styles.cardIcon} src={card.icon} alt="" />
                      </div>
                      <h3 className={styles.cardTitle}>{card.title}</h3>
                      <p className={styles.cardText}>{itemsArr[previousIndex % itemsArr.length]?.title || ''}</p>
                      <button type="button" className={styles.linkButton}>
                        {card.buttonText}
                        <span className={styles.linkDot} aria-hidden="true">
                          <div style={{ width: '80%', height: '80%', color: '#ffffff' }}>
                            <ChevronRight />
                          </div>
                        </span>
                      </button>
                    </div>
                  )}
                  <div
                    ref={cardRef}
                    className={
                      styles.cardContentWrapper +
                      (isCardTransitioning
                        ? (cardDirection === 'right'
                            ? ' ' + styles.insightSlideInRight
                            : ' ' + styles.insightSlideInLeft)
                        : '')
                    }
                    style={{
                      position: isCardTransitioning ? 'absolute' : 'relative',
                      top: 0,
                      left: 0,
                      width: '100%',
                      zIndex: 2,
                      '--insight-card-width': `${insightCardWidth}px`
                    } as React.CSSProperties}
                  >
                    <div className={styles.cardIconCircle} style={{ background: card.iconBg }}>
                      <img className={styles.cardIcon} src={card.icon} alt="" />
                    </div>
                    <h3 className={styles.cardTitle}>{card.title}</h3>
                    <p
                      className={styles.cardText}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToInsightPage(contentLink)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          goToInsightPage(contentLink);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {truncateByWords(
                        (isCardTransitioning && pendingIndex !== null && itemsArr.length > 0)
                          ? itemsArr[pendingIndex % itemsArr.length]?.title || ''
                          : card.content?.title || '',
                        70
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate(card.link)}
                      className={styles.linkButton}
                    >
                      {card.buttonText}
                      <span className={styles.linkDot} aria-hidden="true">
                        <div style={{ width: '80%', height: '80%', color: '#ffffff' }}>
                          <ChevronRight />
                        </div>
                      </span>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.cardSideArrow + ' ' + styles.cardSideArrowRight}
                  onClick={card.onNext}
                  disabled={!card.hasContent}
                  aria-label="Next"
                  style={{ cursor: card.hasContent ? 'pointer' : 'default', opacity: card.hasContent ? 1 : 0.3 }}
                >
                  <div style={{ width: '70%', height: '70%' }}>
                    <ChevronRight />
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section className={styles.stats} ref={statsRef as React.RefObject<HTMLElement>} style={{ backgroundImage: `url(${dotPattern})` }}>
        <div className={styles.statsOverlay} />
        <div className={styles.content}>
          <div className={styles.statsInner}>
            <AnimatedStatItem 
              label="Jurisdictions" 
              value={150} 
              format={(v: number) => {
                if (v < 10000) {
                  return `${v}+`;
                }
                return `${Math.floor(v / 1000)}K`;
              }} 
              isInView={statsInView} 
            />
            <AnimatedStatItem 
              label="Professionals" 
              value={23000} 
              format={(v: number) => {
                if (v < 1000) {
                  return `${v}+`;
                }
                return `${Math.floor(v / 1000)}K+`;
              }} 
              isInView={statsInView} 
            />
            <AnimatedStatItem 
              label="Member Firms" 
              value={120} 
              format={(v: number) => {
                if (v < 1000) {
                  return `${v}+`;
                }
                return `${Math.floor(v / 1000)}K`;
              }} 
              isInView={statsInView} 
            />
            <AnimatedStatItem 
              label="Service Areas" 
              value={3} 
              format={(v: number) => {
                if (v < 1000) {
                  return `${v}+`;
                }
                return `${Math.floor(v / 1000)}K`;
              }} 
              isInView={statsInView} 
            />
          </div>
        </div>
      </section>

      <section className={styles.sectionWhite}>
        <div className={`${styles.content} ${styles.events}`}>
          <h2 className={styles.eventsTitle}>Upcoming Events &amp; Webinars</h2>
          <div className={styles.eventRowContainer}>
            {(() => {
              const filteredEvents = landingData && Array.isArray(landingData.events)
                ? landingData.events.filter((event: any) => event.homePage && event.status === 'online')
                : [];
              const eventsList = filteredEvents;

              function formatEventType(type: string | undefined) {
                if (!type) {
                  return '';
                }
                if (type === 'inPerson') {
                  return 'In Person';
                }
                if (type === 'virtual') {
                  return 'Virtual';
                }
                if (type === 'hybrid') {
                  return 'Hybrid';
                }
                return type;
              }

              const responsive = {
                desktop: {
                  breakpoint: { max: 3000, min: 1200 },
                  items: 3,
                  slidesToSlide: 1,
                  partialVisibilityGutter: 40
                },
                tablet: {
                  breakpoint: { max: 1199, min: 768 },
                  items: 2,
                  slidesToSlide: 1,
                  partialVisibilityGutter: 30
                },
                mobile: {
                  breakpoint: { max: 767, min: 0 },
                  items: 1,
                  slidesToSlide: 1,
                  partialVisibilityGutter: 20
                }
              };
              
              return (
                <Carousel 
                  responsive={responsive}
                  infinite={true}
                  arrows={true}
                  swipeable={true}
                  draggable={true}
                  showDots={false}
                  ssr={false}
                  autoPlay={true}
                  autoPlaySpeed={5000}
                  keyBoardControl={true}
                  customTransition="transform 300ms ease-in-out"
                  transitionDuration={300}
                  removeArrowOnDeviceType={[]}
                  containerClass="event-carousel-container"
                  itemClass="event-carousel-item"
                >
                  {eventsList.map((event: any, idx: number) => {
                    const eventDate = formatLandingEventDate(event, true);
                    const eventImg = event.groupPhotoFallback || event.bannerLists || event.bannerHomePage || event.image || event.coverImage || event.accomodation?.[0]?.image || webinarsImg;
                    return (
                      <div key={event.id || idx} className={styles.eventCard}>
                        <div className={styles.eventImgWrap}>
                          <img className={styles.eventImg} src={eventImg} alt="" />
                        </div>
                        <div className={styles.eventBody}>
                          <div>
                            <div className={styles.eventMetaTitle}>{formatEventType(event.eventType) || event.title || 'Event'}</div>
                            <div className={styles.eventDate}>{eventDate}</div>
                            <div className={styles.eventDesc}>{event.title || ''}</div>
                          </div>
                          <div>
                            <div className={styles.eventOrg}>
                              {Array.isArray(event.sponsorHostNames)
                                ? event.sponsorHostNames.map((line: string, i: number) => (
                                    <div key={i}>{line}</div>
                                  ))
                                : (event.sponsorHostNames || event.eventHostSponsorLabel || 'World Services Group')}
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate(`/events/${event.id || ''}/overview`)}
                              className={`${styles.linkButton} ${styles.eventLink}`}
                            >
                              DETAILS
                              <span className={styles.linkDot} aria-hidden="true">
                                <div style={{ width: '80%', height: '80%', color: '#ffffff' }}>
                                  <ChevronRight />
                                </div>
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </Carousel>
              );
            })()}
          </div>

          <div className={styles.eventsFooter}>
            <button
              type="button"
              onClick={() => navigate('/events')}
              className={`${styles.linkButton} ${styles.eventLink}`}
            >
              ALL EVENTS
              <span className={styles.linkDot} aria-hidden="true">
                <div style={{ width: '80%', height: '80%', color: '#ffffff' }}>
                  <ChevronRight />
                </div>
              </span>
            </button>
          </div>
        </div>
      </section>

      <section className={styles.sectionWhite}>
        <div className={styles.featuresContainer}>
          <button
            type="button"
            className={`${styles.featuresArrow} ${styles.featuresArrowLeft}`}
            aria-label="Previous feature"
            onClick={() => scrollFeatures('left')}
          >
            <div style={{ width: '70%', height: '70%' }}>
              <ChevronLeft />
            </div>
          </button>
          <button
            type="button"
            className={`${styles.featuresArrow} ${styles.featuresArrowRight}`}
            aria-label="Next feature"
            onClick={() => scrollFeatures('right')}
          >
            <div style={{ width: '70%', height: '70%' }}>
              <ChevronRight />
            </div>
          </button>
          <div className={styles.featuresRow} ref={featuresRowRef}>
            <div className={styles.featuresTrack}>
              {loopedFeatures.map((feature, i) => {
                const bgImg = cardBgs[i % cardBgs.length];
                return (
                  <div
                    key={`${feature.title}-${i}`}
                    className={styles.feature}
                    data-feature-item="true"
                    style={{ backgroundImage: `url(${bgImg})`, backgroundPosition: feature.pos }}
                  >
                    <div className={styles.featureBox}>
                      <h3 className={styles.featureText}>{feature.title}</h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default LandingPage;
